import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import DOMPurify from "dompurify";
import { ArrowLeft } from "lucide-react";
import { SectionLabel } from "@/components/Bits";
import { blogSupabase, isBlogConfigured } from "@/lib/blogSupabase";

function formatDate(value) {
  if (!value) return "";
  try {
    return format(new Date(value), "MMMM d, yyyy");
  } catch {
    return "";
  }
}

// The post body is sanitized HTML from a Tiptap rich-text editor. We still
// re-sanitize in the browser before rendering (defence in depth) with an
// allowlist that matches exactly what the editor can produce.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ["h2", "h3", "h4", "p", "strong", "em", "ul", "ol", "li", "a", "img", "br"],
  ALLOWED_ATTR: ["href", "src", "alt", "target", "rel"],
  // Only permit http(s) and mailto URLs on href/src (blocks javascript:, data:, etc.).
  ALLOWED_URI_REGEXP: /^(?:https?:|mailto:)/i,
};

// Harden any link that opens in a new tab against reverse-tabnabbing. Registered
// once at module load; DOMPurify hooks are global to the imported instance.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

function sanitizeBody(html) {
  return DOMPurify.sanitize(html || "", SANITIZE_CONFIG);
}

// The repo has no @tailwindcss/typography plugin, so we style the rendered HTML
// explicitly via arbitrary-variant selectors, mapping each element to the site's
// forest/cream/sage palette + fonts. This mirrors how the old markdown version
// looked so nothing shifts visually.
const ARTICLE_CLASS = [
  "text-forest-400 text-[16px] md:text-[17px] leading-[1.75]",
  // Headings
  "[&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:font-display [&_h2]:font-bold [&_h2]:text-forest-600 [&_h2]:text-2xl md:[&_h2]:text-3xl [&_h2]:leading-tight",
  "[&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:font-display [&_h3]:font-bold [&_h3]:text-forest-600 [&_h3]:text-xl md:[&_h3]:text-2xl [&_h3]:leading-snug",
  "[&_h4]:mt-6 [&_h4]:mb-2 [&_h4]:font-display [&_h4]:font-semibold [&_h4]:text-forest-600 [&_h4]:text-lg",
  // Paragraphs
  "[&_p]:my-4",
  // Lists
  "[&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_ul]:marker:text-sage-300",
  "[&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-2 [&_ol]:marker:text-sage-500",
  "[&_li]:pl-1",
  // Inline emphasis
  "[&_strong]:text-forest-600 [&_strong]:font-semibold",
  "[&_em]:italic",
  // Links
  "[&_a]:text-forest-600 [&_a]:font-medium [&_a]:underline [&_a]:underline-offset-2 [&_a]:decoration-sage-300 hover:[&_a]:text-forest-500 [&_a]:transition-colors",
  // Inline images — responsive, rounded, sensible vertical rhythm
  "[&_img]:my-6 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-xl",
].join(" ");

function CenteredState({ title, children, showBack = true }) {
  return (
    <section className="max-w-[1320px] mx-auto px-5 lg:px-10 pt-16 lg:pt-24 pb-24">
      <div className="max-w-xl" data-reveal>
        <h1 className="font-display font-extrabold tracking-tight text-forest-600 text-3xl md:text-4xl leading-tight">
          {title}
        </h1>
        <p className="mt-4 text-forest-400 text-[15px] md:text-base leading-relaxed">{children}</p>
        {showBack && (
          <Link to="/blogs" className="btn-primary mt-8">
            <ArrowLeft size={16} /> Back to the blog
          </Link>
        )}
      </div>
    </section>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError(false);
      setPost(null);

      if (!isBlogConfigured) {
        setLoading(false);
        return;
      }
      try {
        const { data, error: fetchError } = await blogSupabase
          .from("posts")
          .select("id, title, slug, body, excerpt, meta_description, cover_image, published_at")
          .eq("status", "published")
          .eq("slug", slug)
          .in("site", ["aidify", "both"])
          .maybeSingle();

        if (!active) return;
        if (fetchError) throw fetchError;
        setPost(data || null);
      } catch (e) {
        // TEMPORARY (blog schema-migration cutover): the UI renders a generic
        // "couldn't load the blog" state at HTTP 200, which is indistinguishable
        // from a missing post. Log the real Supabase error so a failed cutover
        // (unexposed schema, missing GRANT, bad key) is visible in the browser
        // console. Remove once the migration is verified.
        console.error("[blogSupabase] Blog post query failed for slug:", slug, e);
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [slug]);

  // The global reveal observer in Layout runs once on mount, before the post is
  // fetched, so the asynchronously-rendered article body never receives the
  // `is-visible` class and stays at opacity:0. Re-run the observer once the post
  // is in the DOM so the content fades in like the rest of the page.
  useEffect(() => {
    if (loading || error || !post) return;
    const els = document.querySelectorAll("[data-reveal]:not(.is-visible)");
    if (!els.length) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => e.isIntersecting && e.target.classList.add("is-visible")),
      { rootMargin: "0px 0px -10% 0px", threshold: 0.05 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [loading, error, post]);

  // Sanitize the HTML body in the browser before it is ever rendered. Runs on
  // every render but is memoized on the raw body so it only re-computes when the
  // post changes. Guarded against null so the hook order stays stable.
  const safeHtml = useMemo(() => sanitizeBody(post?.body), [post?.body]);

  if (loading) {
    return (
      <section className="max-w-[760px] mx-auto px-5 lg:px-10 pt-10 lg:pt-16 pb-24">
        <div className="h-3 w-28 bg-cream-200 rounded animate-pulse" />
        <div className="mt-6 h-10 w-3/4 bg-cream-200 rounded animate-pulse" />
        <div className="mt-4 h-4 w-32 bg-cream-200 rounded animate-pulse" />
        <div className="mt-8 aspect-[16/9] bg-cream-200 rounded-2xl animate-pulse" />
        <div className="mt-8 space-y-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-4 w-full bg-cream-200 rounded animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <CenteredState title="We couldn't load this article">
        Something went wrong while fetching this post. Please refresh or head back to the blog.
      </CenteredState>
    );
  }

  if (!post) {
    return (
      <CenteredState title="Post not found">
        The article you're looking for doesn't exist or may have been moved.
      </CenteredState>
    );
  }

  const description =
    post.meta_description || post.excerpt || `${post.title} — from the Aidify blog.`;

  return (
    <>
      <Helmet>
        <title>{`${post.title} | Aidify Blog`}</title>
        <meta name="description" content={description} />
      </Helmet>

      <article className="max-w-[760px] mx-auto px-5 lg:px-10 pt-10 lg:pt-16 pb-20 lg:pb-28">
        <div data-reveal>
          <Link
            to="/blogs"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-forest-400 hover:text-forest-600 transition-colors"
          >
            <ArrowLeft size={15} /> Back to the blog
          </Link>

          <div className="mt-6">
            <SectionLabel>Article</SectionLabel>
          </div>

          <h1 className="mt-5 font-display font-extrabold tracking-tight text-forest-600 text-3xl md:text-5xl leading-[1.05]">
            {post.title}
          </h1>
          <p className="mt-4 text-sm text-forest-400/80">{formatDate(post.published_at)}</p>
        </div>

        {post.cover_image && (
          <img
            src={post.cover_image}
            alt={post.title}
            className="mt-8 w-full rounded-2xl object-cover"
            data-reveal
          />
        )}

        <div
          className={`mt-8 ${ARTICLE_CLASS}`}
          data-reveal
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
      </article>
    </>
  );
}
